<template>
  <div class="rights">
    <div class="page__header">
      <div class="page__title">{{ $t(`settingsPage.${"userRights"}`) }}</div>
      <div class="buttons">
        <button class="button button--default button--animated" v-if="instance.proxy.$checkRolePermission('permission_create') || instance.proxy.$checkRolePermission('role_create')"><div class="button__icon"></div></button>
        <div class="dropdown" v-if="instance.proxy.$checkRolePermission('permission_create') || instance.proxy.$checkRolePermission('role_create')">
          <div class="dropdown__container">
            <div
              v-if="instance.proxy.$checkRolePermission('role_create')"
              class="dropdown__item"
              @click="store.commit('setShowAddRoleModal', true)"
            >
              {{ $t(`addRoleModal.${"addRoleBtn"}`) }}
            </div>
            <div
              v-if="instance.proxy.$checkRolePermission('permission_create')"
              class="dropdown__item"
              @click="store.commit('setShowAddPermissionModal', true)"
            >
              {{ $t(`addPermissionModal.${"title"}`) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="rights__container">
      <div class="roles box box--default">
        <div class="label label--gradient">{{ $t(`addRoleModal.${"chooseRole"}`) }}</div>
        <Loading v-if="rolesLoading" />
        <div v-else class="roles__body" >

          <div class="user__select" v-if="instance.proxy.$checkRolePermission('team_access')"> 
            <VSelect
              class="select select--default"
              :options="teams"
              :reduce="(team) => team.id" 
              label="name"
              placeholder="Select Team"
              @update:modelValue="onTeamSelected"
              v-model="activeTeamId"
            />
          </div>
   
          <div v-if="instance.proxy.$checkRolePermission('team_access')">
            <div
              class="roles__role"
              :class="{ 'roles__role--selected': selectedRole.id === role.id }"
              v-for="role in roles"
              :key="role.id"
              @click="selectRole(role)"
            >
              {{ role.title }} 
            </div>
          </div>
        </div>
      </div>
      <div class="permissions box box--default">
        <Loading v-if="rolesLoading" />

        <template v-else>
          <div v-if="!permissionsLoading && permissionsData.length > 0" class="label label--gradient">{{ selectedRole.title }}</div>
          <div class="page__header delete-role-btn-wrapper" v-if="!permissionsLoading && permissionsData.length > 0"> 
              <div v-if="instance.proxy.$checkRolePermission('role_delete')" class="icon icon__delete delete-role-btn" @click="deleteReportModal(selectedRole.id)"></div> 
          </div>
          <Loading v-if="permissionsLoading" />
          
          <div
             v-if="instance.proxy.$checkRolePermission('permission_access')"
            class="permissions__body"
            :class="{ 'permissions__body--stretch': !showButtons }"
            ref="permissions"
          > 
         
            <template v-if="!permissionsLoading && permissionsData.length > 0">
              <div
                class="permissions__permission"
                v-for="permission in permissionsData"
                :key="permission.id"
              >
                <span>{{ permission.title }}</span>
                <div class="checkbox" v-if="instance.proxy.$checkRolePermission('permission_edit')">
                  <input
                    type="checkbox"
                    :id="permission.title + permission.id"
                    class="checkbox__checkbox"
                    :checked="permissionIds.includes(permission.id)"
                    @input="togglePermissions($event.target, permission.id)"
                  />
                  <label
                    :for="permission.title + permission.id"
                    class="checkbox__label"
                  ></label>
                </div>
              </div>
            </template>
            <template v-if="!permissionsLoading && permissionsData.length == 0">
              <NoData :type="'teams'" />
            </template>
          </div>
          <div class="permissions__buttons" v-if="showButtons && instance.proxy.$checkRolePermission('permission_access') && instance.proxy.$checkRolePermission('permission_edit')">
            <button
              class="button button--default button--shadow"
              @click="applyPermissions()"
            >
              {{ $t(`buttons.${"saveBtn"}`) }}
            </button>
            <button class="button button--cancel" @click="resetPermissions()">
              {{ $t(`buttons.${"cancelBtn"}`) }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import NoData from "@/partials/NoData";
import Loading from "@/partials/Loading";
import api from "@/api";

const store = useStore();
const router = useRouter();
const instance = getCurrentInstance();

// Template refs
const permissions = ref(null);

// Reactive data
const rolesLoading = ref(false);
const permissionsLoading = ref(false);
const permissionIds = ref([]);
const permissionIdsCopy = ref([]);
const showButtons = ref(false);
const activeTeamId = ref(null);

// Computed properties
const roles = computed(() => store.getters.roles);
const selectedRole = computed(() => store.getters.selectedRole);
const teams = computed(() => store.getters.teams);
const permissionsData = computed(() => store.getters.permissions);

// Methods
const onTeamSelected = async (teamId) => {
  activeTeamId.value = teamId;
  try {
    await store.dispatch("getRolesByTeam", teamId);
    getAllPermissions();
    const [firstRole] = roles.value;
    if (firstRole) {
      store.commit("setSelectedRole", firstRole);
      loadPermissionIds();
    }
  } catch (err) {
    console.error(err);
  }
};

const getAllTeams = async () => {
  rolesLoading.value = true;
  await store.dispatch("getTeams");
  rolesLoading.value = false;
};

const getAllPermissions = async () => {
  permissionsLoading.value = true;
  await store.dispatch("getPermissions");
  permissionsLoading.value = false;
};

const togglePermissions = ({ checked }, permissionId) => {
  const permissionsIndex = permissionIds.value.findIndex(
    (id) => id === permissionId
  );
  if (checked && permissionsIndex === -1) {
    permissionIds.value = [...permissionIds.value, permissionId];
  } else {
    permissionIds.value.splice(permissionsIndex, 1);
  }
};

const selectRole = (roleItem) => {
  store.commit("setSelectedRole", roleItem);
  loadPermissionIds();
  permissions.value.scrollTo(0, 0);
};

const deleteReportModal = (id) => {
  store.commit("setDeleteModal", {
    display: true,
    message: '',
    action: () => deleteRole(id),
    id: id,
  });
};

const deleteRole = async (id) => {
  try {
    await api({ requiresAuth: true }).delete(`/v1/roles/${id}`);
    onTeamSelected(activeTeamId.value);
  } catch (err) {
    console.error(err);
  }
};

const resetPermissions = () => {
  loadPermissionIds();
};

const loadPermissionIds = () => {
  permissionIds.value = selectedRole.value.permissions.map(
    (permission) => permission.id
  );
  permissionIdsCopy.value = [...permissionIds.value];
};

const applyPermissions = async () => {
  try {
    const { data } = await store.dispatch("updateRole", {
      id: selectedRole.value.id,
      permissions: permissionIds.value,
    });

    try {
      await store.dispatch("getRolesByTeam", activeTeamId.value);
      getAllPermissions();
      const { data: roleData } = data;
      if (roleData) {
        store.commit("setSelectedRole", roleData);
        loadPermissionIds();
      }
    } catch (err) {
      console.error(err);
    }

    await store.dispatch(
      "updateLoggedUser",
      JSON.parse(localStorage.getItem("user")).id
    );
  } catch (err) {
    console.error(err);
  }
};

// Watchers
watch(permissionIds, (permissionsValue) => {
  showButtons.value =
    permissionIdsCopy.value.some(
      (permission) => !permissionsValue.includes(permission)
    ) || permissionsValue.length > permissionIdsCopy.value.length;
});

watch(teams, (newTeams) => {
  if (newTeams.length === 1) {
    activeTeamId.value = newTeams[0].id;
    onTeamSelected(activeTeamId.value);
  }
});

// Lifecycle
onMounted(async () => {
  if (!instance.proxy.$checkRolePermission('role_access')) {
    router.push('/dashboard');
  } else {
    store.commit("setRoles", []);
    store.commit("setTeams", []);
    store.commit("setPermissions", []);

    await getAllTeams();

    if (teams.value.length == 1) {
      activeTeamId.value = teams.value[0];

      onTeamSelected(activeTeamId.value.id);
    }
  }
});
</script>

<style scoped lang="scss">
.user__select {
    padding: 0 20px  ;
    margin-bottom: 22px;
}

.delete-role-btn-wrapper{
  justify-content: flex-end
}
.delete-role-btn{
  margin-top: 20px;
  margin-right: 20px;
}

.rights {
  height: 100%;

  .page__header {
    .buttons {
      width: 180px;
      position: relative;
      cursor: pointer;

      .button {
        width: 100%;
      }

      .dropdown {
        position: absolute;
        width: 100%;
        display: none;
        padding: 5px 0;
        &__container {
          position: absolute;
          z-index: 3;
          width: 100%;
          background: var(--bg-2);
          border-radius: var(--radius-1);
          padding: 10px 0;
          top: calc(100% - 5px);

          &::after {
            content: "";
            position: absolute;
            top: -4px;
            left: 50%;
            transform: translateX(-50%);
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 4px solid var(--border-2);
          }
        }

        &__item {
          padding: 10px 12px;
          color: var(--text-1);
          font-size: var(--medium);
          font-family: var(--muli-text-semi-bold);

          &:hover {
            background: var(--bg-19);
          }
        }
      }

      &:hover {
        .button {
          background-color: var(--hover-1);
          box-shadow: 0px -7px 18px var(--shadow-2);
        }
        .dropdown {
          display: block;
        }
      }
    }
  }

  &__container {
    height: calc(100% - 48px - 20px);
    display: flex;
  }

  .roles,
  .permissions {
    height: 100%;
    background: var(--bg-1);
    position: relative;
  }

  .roles__role,
  .permissions__permission {
    color: var(--text-9);
    font-size: var(--medium);
    padding: 10px 20px;
    cursor: pointer;

    &:hover,
    &--selected {
      background: var(--bg-17);
    } 

    
  }

  .permission-msg{
    cursor: initial;
    &:hover,
    &--selected {
      background: transparent;
    } 
  }

  .roles,
  .permissions {
    &__body {
      margin-top: 65px;
      padding-top: 20px;
      overflow: auto;
    }
  }

  .roles {
    width: 400px;
    margin-right: 20px;

    &__body {
      height: calc(100% - 65px);
    }
  }

  .permissions {
    flex: 1;

    &__body {
      max-height: calc(100% - 65px - 88px);

      &--stretch {
        height: calc(100% - 65px);
      }
    }

    &__permission {
      border-bottom: 1px solid var(--border-15);
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px 20px;
    }

    &__buttons {
      display: flex;
      justify-content: center;
      padding: 20px 0;

      .button {
        height: 40px;
        width: 140px;

        &:first-child {
          margin-right: 80px;
        }
      }
    }
  }
}
</style>
